/**
 * Jun 2026 · P17-UAT-ACCOUNTING-YEAR-AUTHORITY-01A · Accounting Year Master (Admin UI).
 *
 * Minimal admin surface over the ALREADY-implemented canonical Accounting Year
 * authority (collection `accounting_years`, API `/accounting-years`, resolver
 * `get_active_accounting_year()`). This screen creates NO new engine, collection
 * or resolver — it only reads/administers the existing master. The backend stays
 * authoritative for the "exactly one valid active + OPEN year" rule and all
 * fail-closed validation; this UI surfaces whatever errors the backend returns.
 *
 *   Accounting Year Master → Active FY → Number Series Master → Document Number
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { RowActions } from "@/components/RowActions";
import { useAuth } from "@/lib/auth";
import { fmtDateIST, fmtDateTime12IST } from "@/lib/datetime";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  CalendarCheckIcon, PlusIcon, PencilIcon, CheckCircleIcon, ArrowsClockwiseIcon,
  EyeIcon, LockIcon, LockOpenIcon, InfoIcon,
} from "@phosphor-icons/react";

const inpCls = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const lblCls = "text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block";

const financialYear = (y) => {
  const s = (y.start_date || "").slice(0, 4);
  const e = (y.end_date || "").slice(0, 4);
  return s && e ? `${s} – ${e}` : "—";
};

const emptyCreate = () => ({ fy_code: "", start_date: "", end_date: "", activate: false });

export default function AccountingYearMaster() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("masters_number_series:edit");

  const [years, setYears] = useState([]);
  const [activeFy, setActiveFy] = useState(null);
  const [cacheValid, setCacheValid] = useState(true);
  const [cacheReason, setCacheReason] = useState("");
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate());
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/accounting-years");
      setYears((data?.years || []).slice().sort((a, b) => (a.fy_code || "").localeCompare(b.fy_code || "")));
      const cache = data?.active_cache || {};
      setActiveFy(cache.fy || null);
      setCacheValid(!!cache.valid);
      setCacheReason(cache.reason || "");
    } catch (_e) {
      toast.error("Failed to load Accounting Years");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const activeRow = useMemo(() => years.find((y) => y.is_active), [years]);

  const openCreate = () => { setCreateForm(emptyCreate()); setCreateOpen(true); };
  const openEdit = (r) => { setEditForm({ ...r }); setEditOpen(true); };

  const submitCreate = async (e) => {
    e.preventDefault();
    const { fy_code, start_date, end_date } = createForm;
    if (!fy_code.trim() || !start_date || !end_date) {
      toast.error("FY Code, Start Date and End Date are required"); return;
    }
    setSaving(true);
    try {
      await api.post("/accounting-years", {
        fy_code: fy_code.trim(), start_date, end_date, activate: !!createForm.activate,
      });
      toast.success(`Accounting Year ${fy_code.trim()} created`);
      setCreateOpen(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editForm.start_date || !editForm.end_date) {
      toast.error("Start Date and End Date are required"); return;
    }
    setSaving(true);
    try {
      await api.patch(`/accounting-years/${editForm.fy_code}`, {
        start_date: editForm.start_date, end_date: editForm.end_date,
      });
      toast.success(`Accounting Year ${editForm.fy_code} updated`);
      setEditOpen(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const activate = async (r) => {
    if (!confirm(`Make ${r.fy_code} the ACTIVE Accounting Year? All other years will be deactivated.`)) return;
    try {
      await api.post(`/accounting-years/${r.fy_code}/activate`);
      toast.success(`${r.fy_code} is now the active Accounting Year`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Activation failed"); }
  };

  const setStatus = async (r, status) => {
    const verb = status === "CLOSED" ? "Close" : "Reopen";
    if (!confirm(`${verb} Accounting Year ${r.fy_code}?`)) return;
    try {
      await api.post(`/accounting-years/${r.fy_code}/status`, { status });
      toast.success(`${r.fy_code} ${status === "CLOSED" ? "closed" : "reopened"}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || `${verb} failed`); }
  };

  return (
    <div className="p-6 space-y-5" data-testid="accounting-year-master-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Master Data · Financial</div>
          <h1 className="font-display text-3xl font-black tracking-tight flex items-center gap-3">
            <CalendarCheckIcon size={26} weight="bold" className="text-[var(--accent)]" />
            Accounting Year
          </h1>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
            Canonical Financial Year authority for the whole MES. The <b className="text-zinc-300">active</b> year
            supplies the FY segment to every document number via the existing Number Series Master —
            numbering never falls back to system/document dates.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <button data-testid="new-accounting-year-btn" onClick={openCreate}
              className="h-10 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
              <PlusIcon size={14} weight="bold" />New Accounting Year
            </button>
          )}
          <button data-testid="accounting-year-refresh" onClick={load}
            className="h-10 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm flex items-center gap-2">
            <ArrowsClockwiseIcon size={13} weight="bold" />Refresh
          </button>
        </div>
      </div>

      {/* Active FY banner */}
      <div className="tactical-card p-4 flex items-start gap-3" data-testid="active-fy-banner">
        <InfoIcon size={18} weight="bold" className={cacheValid ? "text-emerald-400 mt-0.5" : "text-red-400 mt-0.5"} />
        <div className="text-sm">
          {cacheValid && activeFy ? (
            <>
              <span className="text-zinc-400">Active Financial Year:</span>{" "}
              <span className="font-mono font-semibold text-white" data-testid="active-fy-value">{activeFy}</span>
              {activeRow && (
                <span className="text-zinc-500 ml-2">
                  ({fmtDateIST(activeRow.start_date)} → {fmtDateIST(activeRow.end_date)} · {activeRow.status})
                </span>
              )}
            </>
          ) : (
            <span className="text-red-300" data-testid="active-fy-failclosed">
              No single valid active Accounting Year — document numbering is FAIL-CLOSED.
              {cacheReason ? ` (${cacheReason})` : ""}
            </span>
          )}
        </div>
      </div>

      {!canManage && (
        <div className="text-[11px] text-amber-300/80 flex items-center gap-2">
          <LockIcon size={12} weight="bold" /> Read-only view — Accounting Year administration requires the
          <b className="text-amber-200">masters_number_series:edit</b> permission.
        </div>
      )}

      {/* List */}
      <div className="tactical-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">{years.length} Accounting Year(s)</span>
          {loading && <span className="text-[10px] text-zinc-600">loading…</span>}
        </div>
        <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="px-3 py-2 text-left">FY Code</th>
                <th className="px-3 py-2 text-left">Financial Year</th>
                <th className="px-3 py-2 text-left">Start Date</th>
                <th className="px-3 py-2 text-left">End Date</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Active</th>
                <th className="px-3 py-2 text-left">Created By</th>
                <th className="px-3 py-2 text-left">Created At</th>
                <th className="px-3 py-2 text-left">Last Updated</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {years.length === 0 && (
                <tr><td colSpan={10} className="text-center py-8 text-zinc-500 text-xs">
                  No Accounting Years configured.
                </td></tr>
              )}
              {years.map((r, i) => {
                const isOpen = (r.status || "OPEN").toUpperCase() === "OPEN";
                return (
                  <tr key={r.fy_code} data-testid={`ay-row-${r.fy_code}`}
                    className={`border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30 ${r.is_active ? "bg-emerald-500/5" : ""}`}>
                    <td className="px-3 py-2 font-mono text-[var(--accent)] text-sm font-semibold">{r.fy_code}</td>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-300">{financialYear(r)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-300">{fmtDateIST(r.start_date)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-300">{fmtDateIST(r.end_date)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${
                        isOpen ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                               : "bg-zinc-500/15 text-zinc-400 border-zinc-500/30"}`}>
                        {r.status || "OPEN"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {r.is_active
                        ? <span data-testid={`ay-active-${r.fy_code}`} className="inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest bg-blue-500/15 text-blue-300 border-blue-500/30">Active</span>
                        : <span className="text-zinc-600 text-xs">—</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-400">{r.created_by || "—"}</td>
                    <td className="px-3 py-2 text-xs text-zinc-500 font-mono">{fmtDateTime12IST(r.created_at)}</td>
                    <td className="px-3 py-2 text-xs text-zinc-500 font-mono">{fmtDateTime12IST(r.updated_at)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <RowActions
                        testid={`ay-actions-${r.fy_code}`}
                        items={[
                          { label: "View", testid: `ay-view-${r.fy_code}`, onClick: () => setViewRow(r) },
                          { label: "Edit dates", testid: `ay-edit-${r.fy_code}`, show: canManage && isOpen, onClick: () => openEdit(r) },
                          { label: "Activate", tone: "approve", testid: `ay-activate-${r.fy_code}`, show: canManage && !r.is_active, onClick: () => activate(r) },
                          { label: "Close year", tone: "warn", testid: `ay-close-${r.fy_code}`, show: canManage && isOpen, onClick: () => setStatus(r, "CLOSED") },
                          { label: "Reopen year", tone: "approve", testid: `ay-reopen-${r.fy_code}`, show: canManage && !isOpen, onClick: () => setStatus(r, "OPEN") },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-lg" data-testid="ay-create-dialog"
          description="Create a new Accounting Year. FY Code is the immutable FY segment used in document numbers.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              <CalendarCheckIcon size={20} weight="bold" className="text-[var(--accent)]" />
              New Accounting Year
            </DialogTitle>
          </DialogHeader>
          <form id="ay-create-form" onSubmit={submitCreate} className="space-y-3 mt-2">
            <div>
              <label className={lblCls}>FY Code * (e.g. 27-28)</label>
              <input data-testid="ay-create-fycode" type="text" value={createForm.fy_code}
                onChange={(e) => setCreateForm((f) => ({ ...f, fy_code: e.target.value }))}
                className={inpCls} placeholder="27-28" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lblCls}>Start Date *</label>
                <input data-testid="ay-create-start" type="date" value={createForm.start_date}
                  onChange={(e) => setCreateForm((f) => ({ ...f, start_date: e.target.value }))}
                  className={inpCls} required />
              </div>
              <div>
                <label className={lblCls}>End Date *</label>
                <input data-testid="ay-create-end" type="date" value={createForm.end_date}
                  onChange={(e) => setCreateForm((f) => ({ ...f, end_date: e.target.value }))}
                  className={inpCls} required />
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
              <input data-testid="ay-create-activate" type="checkbox" checked={createForm.activate}
                onChange={(e) => setCreateForm((f) => ({ ...f, activate: e.target.checked }))} />
              Activate immediately (deactivates the current active year)
            </label>
          </form>
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => setCreateOpen(false)}
              className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm">Cancel</button>
            <button type="submit" form="ay-create-form" data-testid="ay-create-save" disabled={saving}
              className="h-10 px-5 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2 disabled:opacity-40">
              <CheckCircleIcon size={14} weight="bold" />{saving ? "Saving…" : "Create Year"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-lg" data-testid="ay-edit-dialog"
          description="Edit Accounting Year period boundaries. FY Code cannot be changed.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              <PencilIcon size={20} weight="bold" className="text-[var(--accent)]" />
              Edit Accounting Year
            </DialogTitle>
          </DialogHeader>
          {editForm && (
            <form id="ay-edit-form" onSubmit={submitEdit} className="space-y-3 mt-2">
              <div>
                <label className={lblCls}>FY Code (immutable)</label>
                <input data-testid="ay-edit-fycode" type="text" value={editForm.fy_code} disabled
                  className={`${inpCls} opacity-60 cursor-not-allowed`} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lblCls}>Start Date *</label>
                  <input data-testid="ay-edit-start" type="date" value={(editForm.start_date || "").slice(0, 10)}
                    onChange={(e) => setEditForm((f) => ({ ...f, start_date: e.target.value }))}
                    className={inpCls} required />
                </div>
                <div>
                  <label className={lblCls}>End Date *</label>
                  <input data-testid="ay-edit-end" type="date" value={(editForm.end_date || "").slice(0, 10)}
                    onChange={(e) => setEditForm((f) => ({ ...f, end_date: e.target.value }))}
                    className={inpCls} required />
                </div>
              </div>
            </form>
          )}
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => setEditOpen(false)}
              className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm">Cancel</button>
            <button type="submit" form="ay-edit-form" data-testid="ay-edit-save" disabled={saving}
              className="h-10 px-5 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2 disabled:opacity-40">
              <CheckCircleIcon size={14} weight="bold" />{saving ? "Saving…" : "Update Dates"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewRow} onOpenChange={(o) => !o && setViewRow(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-lg" data-testid="ay-view-dialog"
          description="Accounting Year details (read-only).">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              <EyeIcon size={20} weight="bold" className="text-[var(--accent)]" />
              Accounting Year {viewRow?.fy_code}
            </DialogTitle>
          </DialogHeader>
          {viewRow && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm mt-2">
              <div><div className={lblCls}>FY Code</div><div className="font-mono text-[var(--accent)]">{viewRow.fy_code}</div></div>
              <div><div className={lblCls}>Financial Year</div><div className="font-mono">{financialYear(viewRow)}</div></div>
              <div><div className={lblCls}>Start Date</div><div className="font-mono">{fmtDateIST(viewRow.start_date)}</div></div>
              <div><div className={lblCls}>End Date</div><div className="font-mono">{fmtDateIST(viewRow.end_date)}</div></div>
              <div><div className={lblCls}>Status</div><div className="font-mono">{viewRow.status || "OPEN"}</div></div>
              <div><div className={lblCls}>Active</div><div className="font-mono">{viewRow.is_active ? "YES" : "NO"}</div></div>
              <div><div className={lblCls}>Created By</div><div className="text-zinc-300">{viewRow.created_by || "—"}</div></div>
              <div><div className={lblCls}>Created At</div><div className="font-mono text-xs text-zinc-400">{fmtDateTime12IST(viewRow.created_at)}</div></div>
              <div><div className={lblCls}>Updated By</div><div className="text-zinc-300">{viewRow.updated_by || "—"}</div></div>
              <div><div className={lblCls}>Last Updated</div><div className="font-mono text-xs text-zinc-400">{fmtDateTime12IST(viewRow.updated_at)}</div></div>
            </div>
          )}
          <DialogFooter>
            <button type="button" onClick={() => setViewRow(null)}
              className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
